// ##### Komarov 2025-08-20
import { useEffect, useRef } from 'react';
// ##### Komarov 2025-08-26
import getDev from '../helpers/getDevMode';

const getDevMode = () => getDev();
const devMode = getDevMode();

/**
 * Hook to measure component rendering performance
 * @param {string} componentName - Name of the component being measured
 * @param {Object} [options] - Measurement options
 * @param {boolean} [options.logToConsole=true] - Whether to log measurements to console
 * @param {boolean} [options.measureMemory=false] - Whether to measure memory usage (Node.js only)
 * @param {Function} [options.onMeasure] - Callback with measurement results
 * 
 * @example
 * import { usePerformanceMeasure } from '../../hooks/usePerformanceMeasure';
 * 
 * function MyComponent() {
 *   usePerformanceMeasure('MyComponent', {
 *     logToConsole: true,
 *     measureMemory: false, // Only works in Node.js
 *     onMeasure: (result) => {
 *       console.log('Performance data:', result);
 *       // Send to analytics if needed
 *     }
 *   });
 * 
 *   return <div>My Component</div>;
 * }
 */
export const usePerformanceMeasure = (componentName, options = {}) => {
  const startTimeRef = useRef(null);
  const isServer = typeof window === 'undefined';
  const {
    logToConsole = true,
    measureMemory = false,
    onMeasure,
    // ##### Komarov 2025-08-27
    componentDevMode
  } = options;
  // Client-side measurement
  useEffect(() => {
    if ((componentDevMode || devMode) && !isServer) {
      // Start measurement when component mounts
      startTimeRef.current = performance.now();

      if (logToConsole && (componentDevMode || devMode)) {
        console.info(`⏱️ [Client] ${componentName} render started`);
      }

      // Measure paint time
      requestAnimationFrame(() => {
        const paintTime = performance.now();
        const paintDuration = paintTime - startTimeRef.current;

        if (logToConsole && (componentDevMode || devMode)) {
          console.info(`⏱️ [Client] ${componentName} painted in ${paintDuration.toFixed(2)}ms`);
        }

        // Schedule final measurement
        setTimeout(() => {
          const endTime = performance.now();
          const totalDuration = endTime - startTimeRef.current;

          const result = {
            componentName,
            environment: 'client',
            paintDurationMs: paintDuration,
            totalDurationMs: totalDuration,
            timestamp: new Date().toISOString()
          };

          if (logToConsole && (componentDevMode || devMode)) {
            console.info(`⏱️ [Client] ${componentName} fully rendered in ${totalDuration.toFixed(2)}ms`);
          }

          onMeasure?.(result);
        }, 0);
      });
    }
  }, [componentDevMode, componentName, isServer, logToConsole, onMeasure]);
  if (!componentDevMode && !devMode) return;

  // Server-side measurement
  if (isServer) {
    const start = Date.now();

    // Log start of measurement
    if (logToConsole && (componentDevMode || devMode)) {
      console.info(`⏱️ [Server] ${componentName} render started`);
    }

    // Schedule end measurement after render
    process.nextTick(() => {
      const duration = Date.now() - start;
      const result = {
        componentName,
        environment: 'server',
        durationMs: duration,
        timestamp: new Date().toISOString(),
        memoryUsage: null
      };

      // Measure memory if requested and available
      if (measureMemory && typeof process?.memoryUsage === 'function') {
        try {
          result.memoryUsage = process.memoryUsage();
        } catch (e) {
          console.warn('Could not measure memory usage:', e);
        }
      }

      if (logToConsole && (componentDevMode || devMode)) {
        console.info(`⏱️ [Server] ${componentName} render completed in ${duration}ms`);
        if (result.memoryUsage) {
          console.info('🧠 Memory usage (MB):', {
            rss: Math.round(result.memoryUsage.rss / 1024 / 1024 * 100) / 100,
            heapTotal: Math.round(result.memoryUsage.heapTotal / 1024 / 1024 * 100) / 100,
            heapUsed: Math.round(result.memoryUsage.heapUsed / 1024 / 1024 * 100) / 100,
          });
        }
      }

      onMeasure?.(result);
    });
  }

};

/**
 * Higher-Order Component for measuring performance
 * @param {React.ComponentType} Component - Component to wrap
 * @param {string} [componentName] - Optional custom component name
 * @param {Object} [options] - Options for usePerformanceMeasure
 * 
 * @example
 * import { withPerformanceMeasure } from '../../hooks/usePerformanceMeasure';
 * 
 * const MyComponent = () => {
 *   return <div>My Component</div>;
 * }
 * 
 * export default withPerformanceMeasure(MyComponent, 'MyComponent', {
 *   logToConsole: true
 * });
 */
export const withPerformanceMeasure = (Component, componentName, options = {}) => {
  const displayName = componentName || Component.displayName || Component.name || 'Component';

  const WrappedComponent = (props) => {
    usePerformanceMeasure(displayName, {
      ...options,
      onMeasure: (result) => {
        // You can add additional logging or analytics here
        options.onMeasure?.(result);
      }
    });

    return <Component {...props} />;
  };

  WrappedComponent.displayName = `WithPerformanceMeasure(${displayName})`;
  return WrappedComponent;
};

/**
 * Creates a performance measurement for a specific operation
 * @param {string} operationName - Name of the operation to measure
 * @returns {Object} An object with start() and stop() methods
 * 
 * @example
 * 
 * import { createPerformanceMeasure } from '../../hooks/usePerformanceMeasure';
 * 
 * function fetchData() {
 *   const measure = createPerformanceMeasure('fetchData');
 *   measure.start();
 *   
 *   // Your async operation
 *   return fetch('/api/data')
 *     .then(res => res.json())
 *     .finally(() => {
 *       const result = measure.stop();
 *       console.log('Operation took:', result.durationMs, 'ms');
 *     });
 * }
 */
export const createPerformanceMeasure = (operationName) => {
  let startTime;
  const isServer = typeof window === 'undefined';
  const getDevLocal = () => getDev();

  return {
    start: () => {
      if (!getDevLocal()) return null;
      startTime = isServer && process.hrtime ?
        process.hrtime() :
        performance.now();
      console.info(`⏱️ [Performance] ${operationName} started`);
      return startTime;
    },
    stop: () => {
      if (!getDevLocal()) {
        return {
          operationName,
          startTime: null,
          endTime: null,
          durationMs: 0,
          environment: isServer ? 'server' : 'client'
        };
      }
      if (!startTime) {
        console.warn(`No measurement started for ${operationName}`);
        return null;
      }

      let durationMs;

      if (isServer && process.hrtime) {
        const [seconds, nanoseconds] = process.hrtime(startTime);
        durationMs = (seconds * 1000) + (nanoseconds / 1e6);
      } else {
        const endTime = performance.now();
        durationMs = typeof startTime === 'number' ?
          endTime - startTime :
          performance.now() - startTime[0] * 1000;
      }

      console.info(`⏱️ [Performance] ${operationName} completed in ${durationMs.toFixed(2)}ms`);

      return {
        operationName,
        startTime: isServer ? 'server_time' : startTime,
        endTime: isServer ? 'server_time' : (startTime + durationMs),
        durationMs,
        environment: isServer ? 'server' : 'client'
      };
    }
  };
};
